import { AfterViewInit, Directive, ElementRef, Injector, OnInit, OnDestroy } from '@angular/core';
import { runtimeEventManager } from '@wm/core';
import { forEach, get } from "lodash-es";
import { Subscription } from 'rxjs';

@Directive({
    selector: '[wmCaptionPosition]',
    standalone: true,
    exportAs: 'wmCaptionPosition'
})
export class CaptionPositionDirective implements AfterViewInit, OnDestroy {
    private elementRef: ElementRef;
    private nativeEl: HTMLElement;
    private compositeEle: HTMLElement | null;
    private inputEl: JQuery<HTMLElement>;
    private _isPlaceholderBound?: string | null;
    private placeholder?: string | any ;
    private applyFloatingCaptionSub?: Subscription;

    // skip floating caption for the below form fields
    private skipFloatPositionWidgets: string[] = ['wm-radioset', 'wm-checkboxset', 'wm-richtext', 'wm-switch', 'wm-chips', 'wm-checkbox', 'wm-slider', 'wm-rating', 'wm-toggle', 'wm-iconbutton-toggle', 'wm-fileupload'];

    constructor(inj: Injector) {
        this.elementRef = inj.get(ElementRef);
        this.nativeEl = this.elementRef.nativeElement;
        this.applyFloatingCaptionSub =runtimeEventManager.on('apply-floating-caption').subscribe(() => {
          this.settingsForFloatingCaption();
        });
      this.placeholder = $(this.nativeEl).find('input, select, textarea').attr('placeholder');

    }

    ngAfterViewInit() {
      const widget: any = get(this.nativeEl, 'widget');
      let captionPosition: string = widget.$attrs.get('captionposition');
      this._isPlaceholderBound = this.nativeEl.getAttribute('placeholder.bind');

      if (widget.form) {
        captionPosition = widget.form.nativeElement.getAttribute('captionposition');
        this.compositeEle = this.nativeEl.querySelector('.app-composite-widget');
      } else {
        this.compositeEle = this.nativeEl;

        let widgetType = this.compositeEle && $(this.compositeEle).find('label').siblings().children().attr('widgettype');
        if (widgetType && (this.skipFloatPositionWidgets.indexOf(widgetType) > -1)) {
          // remove float position from options, writing seperate check for checkbox widget as the markup is different for wm-checkbox
          widget.widgetProps['captionposition'].options.splice(widget.widgetProps['captionposition'].options.length - 1, 1);
        }
      }
      if (widget.form) { // for form-fields remove caption-floating and replace it with caption-float or caption-top
        // @ts-ignore
        const widgetType: string = this.nativeEl.getAttribute('widget') || this.nativeEl.getAttribute('widgettype'); // fetches the form field type
        if (captionPosition  === 'floating' && this.skipFloatPositionWidgets.indexOf(widgetType) > -1) {
          this.compositeEle?.classList.remove('caption-floating');
          this.compositeEle?.classList.add('caption-top');
        } else {
          this.compositeEle?.classList.remove('caption-floating', 'caption-top', 'caption-left', 'caption-right');
          this.compositeEle?.classList.add('caption-' + captionPosition);
        }
        let _captionClass = widget.form._captionClass;
        let _widgetClass = widget.form._widgetClass;
        if (this.compositeEle) {
          let $el = $(this.compositeEle);
          $el.find('.formfield-label').removeClass().addClass( 'app-label control-label formfield-label ' + _captionClass);
          _widgetClass && $el.find('.formfield-label + div').removeClass().addClass(widget.form._widgetClass);
          if (widget.required) {
            $el.find('.formfield-label').addClass('required');
          }
          // adding hidden when readonly mode is set on the form.
          $el.find("[formControlName]").removeClass('hidden');
          if (!widget.form.updateMode && widget.viewmodewidget !== 'default') {
            $el.find('[formControlName]').addClass('hidden');
          }
        }
        if (this.placeholder)
          $(this.nativeEl).find('input, select, textarea').attr('placeholder', this.placeholder);

        if (captionPosition === 'floating') {
          this.settingsForFloatingCaption();
        }
        this.checkForRightAlignedForm();
      }
    }

  public settingsForFloatingCaption() {
    this.inputEl = $(this.nativeEl).find('input, select, textarea');
    this.placeholder = this.inputEl.attr('placeholder');
    if (!this._isPlaceholderBound) {
      this._isPlaceholderBound = this.inputEl.closest('[widget-id]').attr('placeholder.bind');
    }
    // remove placeholder when the input el has placeholder and the placeholder is not bound to any variable
    if (!this._isPlaceholderBound && this.inputEl.attr('placeholder')) {
      this.inputEl.removeAttr('placeholder');
    }

    this.inputEl.off('focus.floatCaption blur.floatCaption');
    if (this.shouldActivateFloatState()) {
      this.compositeEle?.classList.add('float-active');
      if (!this.inputEl.val()) {
        this.inputEl.val(this.nativeEl.getAttribute('defaultvalue') || '');
      }
    }
    this.inputEl.on('focus.floatCaption', () => this.onFocusCb());
    this.inputEl.on('blur.floatCaption',  () => this.onBlurCb());
  }

  /**
   * Determines whether the floating caption state (`float-active` class) should be applied.
   * 
   * A floating caption is activated if any of the following conditions are met:
   * 1. The input element has a value and is not a <select>.
   * 2. The closest widget container has a `datavalue` attribute.
   * 3. The parent widget container has a `datavalue.bind` attribute.
   * 4. The native element defines a `defaultvalue` attribute.
   * 5. The native element defines a `displayformat` attribute.
   * 6. A <select> element has a currently selected option with non-empty text.
   * 7. The <select> element supports multiple selections (`multiple` attribute present).
   * 
   * These conditions ensure that the floating caption is shown when the field is pre-filled,
   * has formatting rules, or contains any selected values, even before user interaction.
   *
   * @returns {boolean} True if the floating caption should be activated; otherwise false.
   */
  private shouldActivateFloatState() {
    return (this.inputEl.val() && !this.inputEl.is('select')) || $(this.inputEl.closest('[widget-id]')).attr('datavalue') || $(this.inputEl.parent('[widget-id]')).attr('datavalue.bind') || this.nativeEl.getAttribute('defaultvalue')
      || this.nativeEl.getAttribute('displayformat') || $(this.nativeEl).find('select option:selected').text() || $(this.nativeEl).find('select').attr('multiple');
  }

  private onBlurCb() { // on blur, remove animation and placeholder if there is no value
    let mobileInputVal;
    // In case of mobile date / time picker check for input element's value which has 'mobile-input' class
    if (this.inputEl.length > 1 && this.inputEl.hasClass('mobile-input')) {
      forEach(this.inputEl, (el: any) => {
        if ($(el).hasClass('mobile-input') && el.value) {
          mobileInputVal = el.value;
        }
      });
    }
    if (!this.inputEl.val() && !mobileInputVal) {
      this.compositeEle?.classList.remove('float-active');
      this.inputEl.removeAttr('placeholder');
    }
  }

  private onFocusCb() { //  on focus, add animation class and the place holder
    this.compositeEle?.classList.add('float-active');
    this.inputEl.attr('placeholder', this.placeholder);
  }

    // when a form is right aligned and have input-group-btn's like date picker, time picker etc. adjust the css to not overlap the label on the icon
    private checkForRightAlignedForm() {
      if (this.compositeEle) {
        const $compositeEle = $(this.compositeEle);
        if ($compositeEle.closest('.align-right').length && $compositeEle.find('.input-group-btn').length) {
          const $label = $compositeEle.find('label');
          if ($compositeEle.find('[wmdatetime]').length) { // for datetime picker, as there will be 2 icons css is different
            $label.addClass('input-grp-dt-icon');
          } else {
            $label.addClass('input-grp-icon');
          }
        }
      }
    }

    ngOnDestroy() {
      // Remove the listeners
      $(this.nativeEl)
      .find('input, select, textarea')
      .off('.floatCaption'); 
      this.applyFloatingCaptionSub?.unsubscribe();
    }
}


